/**
 * Personalization Composable - Dynamic Experience Engine
 *
 * Extracts visitor concerns from onboarding data and provides
 * personalized content based on their specific needs and test results.
 *
 * This turns the test into a selling machine by:
 * 1. Identifying visitor concerns (ADHD, giftedness, sensitivity, etc.)
 * 2. Highlighting relevant test results
 * 3. Personalizing CTAs and messaging
 * 4. Building trust through relevant insights
 */

import { computed } from 'vue'
import { useWizardStore } from '@/stores/wizard'
import type { TestScores } from '@monorepo/types'
import {
  getAccusativePronoun,
  getPossessiveAccusative,
  getPossessiveDative
} from '@monorepo/shared-utils'

// ===========================
// TYPES
// ===========================

export type ConcernType = 'ADHD' | 'GIFTEDNESS' | 'SENSITIVITY' | 'LEARNING_STRUGGLES' | 'PROCESSING' | 'NONE'

export interface PersonalizationProfile {
  concerns: ConcernType[]
  dominantConcern: ConcernType
  childName: string
  childSituation: string
  dominantLearningStyle: string
  relevantScores: Partial<TestScores>
  personalizedHeadline: string
  personalizedDescription: string
  urgencyMessage: string
  trustIndicators: string[]
}

// ===========================
// CONCERN KEYWORDS
// ===========================

const CONCERN_KEYWORDS: Record<ConcernType, string[]> = {
  ADHD: ['konzentration', 'unruhe', 'hyperaktiv', 'adhs', 'aufmerksamkeit', 'impulsiv', 'zappelig'],
  GIFTEDNESS: ['hochbegabt', 'begabt', 'intelligent', 'schnell', 'langweilig', 'unterfordert', 'hochleistung'],
  SENSITIVITY: ['angst', 'stress', 'sensibel', 'überempfindlich', 'reizüberflutung', 'emotional', 'ängstlich'],
  LEARNING_STRUGGLES: ['probleme', 'schwierigkeiten', 'lese', 'schreib', 'rechnen', 'mathe', 'deutsch', 'schulische'],
  PROCESSING: ['verarbeitung', 'verarbeitet', 'langsam', 'schnell', 'auditiv', 'visuell', 'motorisch'],
  NONE: []
}

// ===========================
// COMPOSABLE
// ===========================

export function usePersonalization() {
  //TODO: isnt onboarding Store better?
  const wizardStore = useWizardStore()

  /**
   * Extract concerns from child situation text
   */
  const extractConcerns = (situation: string): ConcernType[] => {
    if (!situation) return []

    const lowerSituation = situation.toLowerCase()
    const foundConcerns: ConcernType[] = []

    Object.entries(CONCERN_KEYWORDS).forEach(([concern, keywords]) => {
      if (concern === 'NONE') return

      const hasKeyword = keywords.some(keyword => lowerSituation.includes(keyword))
      if (hasKeyword) {
        foundConcerns.push(concern as ConcernType)
      }
    })

    return foundConcerns.length > 0 ? foundConcerns : ['NONE']
  }

  /**
   * Get dominant learning style from scores
   */
  const getDominantLearningStyle = (scores: TestScores): string => {
    const learningStyles = {
      visual: scores.visual ?? 0,
      auditory: scores.auditory ?? 0,
      reading: scores.reading ?? 0,
      kinesthetic: scores.kinesthetic ?? 0
    }

    const maxScore = Math.max(...Object.values(learningStyles))
    const dominant = Object.entries(learningStyles).find(([_, score]) => score === maxScore)?.[0]

    const styleNames: Record<string, string> = {
      visual: 'Visueller',
      auditory: 'Auditiver',
      reading: 'Lese-/Schreib',
      kinesthetic: 'Kinästhetischer'
    }

    return styleNames[dominant || 'visual'] || 'Visueller'
  }

  /**
   * Get relevant scores based on concerns
   */
  const getRelevantScores = (concerns: ConcernType[], scores: TestScores): Partial<TestScores> => {
    const relevant: Partial<TestScores> = {}

    // Always include learning styles
    relevant.visual = scores.visual
    relevant.auditory = scores.auditory
    relevant.reading = scores.reading
    relevant.kinesthetic = scores.kinesthetic

    // Add concern-specific scores
    if (concerns.includes('ADHD')) {
      relevant.adhd = scores.adhd
    }
    if (concerns.includes('GIFTEDNESS')) {
      relevant.giftedness = scores.giftedness
    }
    if (concerns.includes('SENSITIVITY')) {
      relevant.highSensitivity = scores.highSensitivity
    }
    if (concerns.includes('PROCESSING')) {
      relevant.auditoryProcessing = scores.auditoryProcessing
    }

    return relevant
  }

  /**
   * Generate personalized headline
   */
  const generateHeadline = (
    childName: string,
    dominantStyle: string,
    concerns: ConcernType[],
    gender?: string
  ): string => {
    const accusativePronoun = getAccusativePronoun(gender)
    const baseHeadline = `${childName} ist ein ${dominantStyle}-Lerner`

    if (concerns.includes('GIFTEDNESS')) {
      return `${baseHeadline} - und möglicherweise hochbegabt!`
    }
    if (concerns.includes('ADHD')) {
      return `${baseHeadline} - mit besonderen Aufmerksamkeitsbedürfnissen`
    }
    if (concerns.includes('SENSITIVITY')) {
      return `${baseHeadline} - mit hoher Sensibilität`
    }

    return `${baseHeadline} - Entdecken Sie ${accusativePronoun} volles Potenzial!`
  }

  /**
   * Generate personalized description
   */
  const generateDescription = (
    childName: string,
    concerns: ConcernType[],
    gender?: string
  ): string => {
    const accusativePronoun = getAccusativePronoun(gender)
    const possessiveAccusative = getPossessiveAccusative(gender)
    const dativePronoun = getPossessiveDative(gender)

    if (concerns.includes('GIFTEDNESS')) {
      return `Die Phase 2 Analyse wird zeigen, ob ${childName} hochbegabt ist und wie Sie ${possessiveAccusative} Potenzial optimal fördern können.`
    }
    if (concerns.includes('ADHD')) {
      return `Die erweiterte Analyse wird Aufschluss über ${childName}'s Aufmerksamkeitsmuster geben und konkrete Strategien bieten.`
    }
    if (concerns.includes('SENSITIVITY')) {
      return `Verstehen Sie ${childName}'s Sensibilität besser und lernen Sie, wie Sie ${accusativePronoun} optimal unterstützen können.`
    }
    if (concerns.includes('LEARNING_STRUGGLES')) {
      return `Die detaillierte Analyse wird zeigen, wie ${childName} am besten lernt und wo ${dativePronoun} Unterstützung braucht.`
    }

    return `Entdecken Sie ${childName}'s einzigartigen Lernstil und wie Sie ${accusativePronoun} optimal fördern können.`
  }

  /**
   * Generate urgency message
   */
  const generateUrgencyMessage = (childName: string, concerns: ConcernType[]): string => {
    if (concerns.includes('GIFTEDNESS')) {
      return `Entdecken Sie jetzt, ob ${childName} hochbegabt ist`
    }
    if (concerns.includes('ADHD')) {
      return `Verstehen Sie ${childName}'s Aufmerksamkeitsmuster besser`
    }
    if (concerns.includes('SENSITIVITY')) {
      return `Lernen Sie, ${childName}'s Sensibilität zu unterstützen`
    }

    return `Erhalten Sie maßgeschneiderte Strategien für ${childName}`
  }

  /**
   * Generate trust indicators based on concerns
   */
  const generateTrustIndicators = (concerns: ConcernType[]): string[] => {
    const baseIndicators = ['Wissenschaftlich fundiert', 'Von Pädagogen empfohlen']

    if (concerns.includes('GIFTEDNESS')) {
      return [...baseIndicators, 'Hochbegabungs-Experten', 'Validierte Methoden']
    }
    if (concerns.includes('ADHD')) {
      return [...baseIndicators, 'ADHD-Spezialisten', 'Evidenzbasiert']
    }
    if (concerns.includes('SENSITIVITY')) {
      return [...baseIndicators, 'Sensibilität-Experten', 'Ganzheitlicher Ansatz']
    }

    return [...baseIndicators, 'Über 15.000 Familien vertrauen uns', 'Lebenslange Unterstützung']
  }

  /**
   * Build complete personalization profile
   */
  const personalizationProfile = computed<PersonalizationProfile>(() => {
    const childName = wizardStore.basicInfo?.childName || 'Ihr Kind'
    const situation = wizardStore.basicInfo?.childSituation || ''
    const gender = wizardStore.basicInfo?.childGender
    const concerns = extractConcerns(situation)
    const dominantConcern = concerns[0] || 'NONE'
    const dominantStyle = getDominantLearningStyle(wizardStore.scores)
    const relevantScores = getRelevantScores(concerns, wizardStore.scores)

    return {
      concerns,
      dominantConcern,
      childName,
      childSituation: situation,
      dominantLearningStyle: dominantStyle,
      relevantScores,
      personalizedHeadline: generateHeadline(childName, dominantStyle, concerns, gender),
      personalizedDescription: generateDescription(childName, concerns, gender),
      urgencyMessage: generateUrgencyMessage(childName, concerns),
      trustIndicators: generateTrustIndicators(concerns)
    }
  })

  /**
   * Check if specific concern is present
   */
  const hasConcern = (concern: ConcernType): boolean => {
    return personalizationProfile.value.concerns.includes(concern)
  }

  /**
   * Get concern-specific content
   */
  const getConcernContent = (concern: ConcernType): Record<string, string> => {
    const contentMap: Record<ConcernType, Record<string, string>> = {
      ADHD: {
        title: 'ADHD-Screening Ergebnisse',
        description: 'Verstehen Sie die Aufmerksamkeitsmuster Ihres Kindes',
        cta: 'Detaillierte ADHD-Analyse'
      },
      GIFTEDNESS: {
        title: 'Hochbegabungs-Indikatoren',
        description: 'Erkennen Sie versteckte Talente und Hochbegabung',
        cta: 'Hochbegabungs-Bericht erhalten'
      },
      SENSITIVITY: {
        title: 'Hochsensibilität-Profil',
        description: 'Verstehen Sie die emotionale Sensibilität Ihres Kindes',
        cta: 'Sensibilität-Analyse'
      },
      LEARNING_STRUGGLES: {
        title: 'Lernstrategien',
        description: 'Maßgeschneiderte Lösungen für Lernherausforderungen',
        cta: 'Strategien erhalten'
      },
      PROCESSING: {
        title: 'Verarbeitungsstil',
        description: 'Wie Ihr Kind Informationen verarbeitet',
        cta: 'Verarbeitungs-Analyse'
      },
      NONE: {
        title: 'Lernprofil',
        description: 'Entdecken Sie den einzigartigen Lernstil',
        cta: 'Mehr erfahren'
      }
    }

    return contentMap[concern]
  }

  return {
    personalizationProfile,
    hasConcern,
    getConcernContent,
    extractConcerns,
    getDominantLearningStyle,
    getRelevantScores
  }
}
